import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2, RefreshCw, LayoutDashboard, Activity, ListChecks, Cpu, HeartPulse,
  Users, Coins, CreditCard, ShoppingCart, CalendarDays, Church, Sparkles,
  DollarSign, Webhook, Receipt, AlertTriangle, FileText, Zap, ChevronDown, PlayCircle,
  Download, Search, ArrowUpDown, ExternalLink,
} from "lucide-react";
import "./AdminDashboard.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "—");
const who = (u) => (u && (u.email || u.name)) ? (u.email || u.name) : "—";
const ms = (v) => (v == null ? "—" : `${(v / 1000).toFixed(1)}s`);
const usd = (v) => (v == null ? "—" : `$${Number(v).toFixed(4)}`);
const num = (v) => (v == null ? "—" : Number(v).toLocaleString());

const NAV = [
  { section: "Operations", items: [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { key: "activity", label: "Live Activity", icon: Activity },
    { key: "jobs", label: "Jobs", icon: ListChecks },
    { key: "ai-usage", label: "AI Usage", icon: Cpu },
    { key: "health", label: "Health", icon: HeartPulse },
  ]},
  { section: "Customers", items: [
    { key: "users", label: "Users", icon: Users },
    { key: "credits", label: "Credits", icon: Coins },
    { key: "subscriptions", label: "Subscriptions", icon: CreditCard },
    { key: "purchases", label: "Purchases", icon: ShoppingCart },
  ]},
  { section: "Products", items: [
    { key: "product-sundaybuilder", label: "SundayBuilder™", icon: CalendarDays },
    { key: "product-parish", label: "Parish Intelligence™", icon: Church },
    { key: "product-future", label: "Future Products", icon: Sparkles },
  ]},
  { section: "Billing", items: [
    { key: "stripe", label: "Stripe", icon: DollarSign },
    { key: "webhooks", label: "Webhooks", icon: Webhook },
    { key: "transactions", label: "Transactions", icon: Receipt },
  ]},
  { section: "Support", items: [
    { key: "failed-jobs", label: "Failed Jobs", icon: AlertTriangle },
    { key: "retry-queue", label: "Retry Queue", icon: RefreshCw },
    { key: "audit-log", label: "Audit Log", icon: FileText },
  ]},
];

const VIEW_ENDPOINTS = {
  dashboard: ["overview", "health", "ai-usage", "activity"],
  activity: ["activity"], jobs: ["generations"], "ai-usage": ["ai-usage"], health: ["health", "webhook-health"],
  users: ["credits"], credits: ["credit-history", "credits"], subscriptions: ["credits"], purchases: ["purchases"],
  "product-sundaybuilder": ["generations"], "product-parish": ["generations"], "product-future": [],
  stripe: ["purchases"], webhooks: ["webhook-events"], transactions: ["credit-history"],
  "failed-jobs": ["failed-generations"], "retry-queue": ["generations"], "audit-log": ["credit-history"],
};

// Some endpoints live under /admin/billing/*, others directly under /admin/*.
const EP_PATH = {
  overview: "billing/overview", credits: "billing/credits", "credit-history": "billing/credit-history",
  purchases: "billing/purchases", "webhook-events": "billing/webhook-events", "failed-generations": "billing/failed-generations",
  "ai-usage": "ai-usage", activity: "activity", health: "health", generations: "generations",
  "webhook-health": "webhook-health",
};

// ---- CSV export ----
const csvCell = (v) => {
  const s = v == null ? "" : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const downloadCsv = (name, columns, rows) => {
  const header = columns.map((c) => c.label).join(",");
  const lines = rows.map((r) => columns.map((c) => csvCell(c.val ? c.val(r) : "")).join(","));
  const blob = new Blob([[header, ...lines].join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
};

// ---- Reusable sortable + searchable table ----
function DataTable({ testid, gridClass, columns, rows, rowKey, rowTestid, emptyText = "No records yet.", csvName, extraRow }) {
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [dir, setDir] = useState(1);
  const searchable = columns.some((c) => c.search);

  let data = rows || [];
  if (q && searchable) {
    const s = q.toLowerCase();
    const scols = columns.filter((c) => c.search);
    data = data.filter((r) => scols.some((c) => String(c.val ? c.val(r) : "").toLowerCase().includes(s)));
  }
  if (sortKey) {
    const col = columns.find((c) => c.key === sortKey);
    if (col && col.val) {
      data = [...data].sort((a, b) => {
        const av = col.val(a), bv = col.val(b);
        if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
        return String(av ?? "").localeCompare(String(bv ?? "")) * dir;
      });
    }
  }
  const clickSort = (c) => { if (!c.sortable) return; if (sortKey === c.key) setDir((d) => -d); else { setSortKey(c.key); setDir(1); } };

  return (
    <div className="fcc-tablewrap" data-testid={testid}>
      {(searchable || csvName) && (
        <div className="fcc-tabletools">
          {searchable && (
            <div className="fcc-searchbox">
              <Search size={14} />
              <input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testid}-search`} />
            </div>
          )}
          {csvName && (
            <button className="fcc-csv" onClick={() => downloadCsv(csvName, columns, data)} data-testid={`${testid}-csv`}>
              <Download size={14} /> Export CSV
            </button>
          )}
        </div>
      )}
      <div className={`fcc-table ${gridClass || ""}`}>
        <div className="fcc-tr fcc-th">
          {columns.map((c) => (
            <span key={c.key} className={c.sortable ? "srt" : ""} onClick={() => clickSort(c)}>
              {c.label}{c.sortable && <ArrowUpDown size={11} className="srt-i" />}{sortKey === c.key ? (dir > 0 ? " ↑" : " ↓") : ""}
            </span>
          ))}
        </div>
        {data.length === 0 ? (
          <div className="fcc-empty" data-testid={`${testid}-empty`}><FileText size={18} /> {emptyText}</div>
        ) : data.map((r) => (
          <div className="fcc-tr" key={rowKey(r)} data-testid={rowTestid ? rowTestid(r) : undefined}>
            {columns.map((c) => <span key={c.key} className={c.className}>{c.render ? c.render(r) : (c.val ? c.val(r) : "")}</span>)}
          </div>
        ))}
      </div>
      {extraRow}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [state, setState] = useState("loading"); // loading | denied | ready
  const [view, setView] = useState("dashboard");
  const [store, setStore] = useState({});
  const [busy, setBusy] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [grant, setGrant] = useState({ email: "", amount: "1" });
  const [remove, setRemove] = useState({ email: "", amount: "1" });
  const [toolMsg, setToolMsg] = useState(null);
  const [testGen, setTestGen] = useState(null);
  const [acting, setActing] = useState("");
  const [selfTest, setSelfTest] = useState(null);
  const [testing, setTesting] = useState(false);

  const loadEndpoint = useCallback(async (ep) => {
    try {
      const r = await axios.get(`${API}/admin/${EP_PATH[ep] || ep}`);
      setStore((prev) => ({ ...prev, [ep]: r.data }));
      return true;
    } catch (e) {
      if (e.response?.status === 403) setState("denied");
      return false;
    }
  }, []);

  const loadView = useCallback(async (v, force) => {
    setBusy(true);
    const eps = VIEW_ENDPOINTS[v] || [];
    await Promise.all(eps.map((ep) => (force || !store[ep]) ? loadEndpoint(ep) : Promise.resolve()));
    setBusy(false);
  }, [loadEndpoint, store]);

  useEffect(() => {
    (async () => {
      const ok = await loadEndpoint("overview");
      if (ok) { setState("ready"); await loadEndpoint("credits"); }
    })();
  }, [loadEndpoint]);

  useEffect(() => { if (state === "ready") loadView(view, false); /* eslint-disable-next-line */ }, [state, view]);

  const refresh = () => { loadEndpoint("overview"); loadView(view, true); };

  // ---- Founder quick actions ----
  const doGrant = async () => {
    setToolMsg(null);
    if (!grant.email || !(parseInt(grant.amount, 10) > 0)) { setToolMsg({ ok: false, text: "Enter an email and a positive amount." }); return; }
    try {
      const r = await axios.post(`${API}/admin/billing/grant-credit`, { email: grant.email, amount: parseInt(grant.amount, 10) });
      setToolMsg({ ok: true, text: `Granted ${grant.amount} credit(s). ${r.data.email} now has ${r.data.credits}.` });
      loadEndpoint("overview"); loadEndpoint("credits"); loadEndpoint("credit-history");
    } catch (e) { setToolMsg({ ok: false, text: e.response?.data?.detail || "Could not grant credit." }); }
  };
  const doRemove = async () => {
    setToolMsg(null);
    if (!remove.email || !(parseInt(remove.amount, 10) > 0)) { setToolMsg({ ok: false, text: "Enter an email and a positive amount." }); return; }
    try {
      const r = await axios.post(`${API}/admin/billing/remove-credit`, { email: remove.email, amount: parseInt(remove.amount, 10) });
      setToolMsg({ ok: true, text: `Removed ${r.data.removed} credit(s). ${r.data.email} now has ${r.data.credits}.` });
      loadEndpoint("overview"); loadEndpoint("credits"); loadEndpoint("credit-history");
    } catch (e) { setToolMsg({ ok: false, text: e.response?.data?.detail || "Could not remove credit." }); }
  };
  const pollTestGen = (jobId, n) => {
    const tick = async () => {
      try {
        const jr = await axios.get(`${API}/parish/job/${jobId}`);
        if (jr.data.status === "done") { setTestGen({ status: "completed", jobId, report: jr.data.report }); loadEndpoint("overview"); loadEndpoint("ai-usage"); }
        else if (jr.data.status === "error") { setTestGen({ status: "failed", jobId, error: jr.data.detail, errorCode: jr.data.error_code }); loadEndpoint("overview"); }
        else if (n > 40) setTestGen({ status: "failed", jobId, error: "Timed out waiting for the test generation." });
        else setTimeout(() => pollTestGen(jobId, n + 1), 3000);
      } catch { if (n > 40) setTestGen({ status: "failed", jobId, error: "Lost contact while polling." }); else setTimeout(() => pollTestGen(jobId, n + 1), 3000); }
    };
    setTimeout(tick, 2500);
  };
  const doTestGen = async () => {
    setToolMsg(null); setTestGen({ status: "starting", jobId: null });
    try {
      const r = await axios.post(`${API}/admin/billing/test-generate`, { scope: "Single Sunday", occasion: "", details: "" });
      setTestGen({ status: "in_progress", jobId: r.data.job_id });
      loadEndpoint("overview"); pollTestGen(r.data.job_id, 0);
    } catch (e) { setTestGen({ status: "failed", jobId: null, error: e.response?.data?.detail || "Could not start test generation." }); }
  };
  const doRetry = async (jobId) => {
    setActing(jobId); setToolMsg(null);
    try {
      await axios.post(`${API}/admin/billing/retry-generation/${jobId}`);
      setToolMsg({ ok: true, text: "Retry started (no credit charged)." });
      loadEndpoint("overview"); loadEndpoint("failed-generations"); loadEndpoint("generations");
    } catch (e) { setToolMsg({ ok: false, text: e.response?.data?.detail || "Could not retry generation." }); }
    setActing("");
  };
  const doReplay = async (eventId) => {
    setActing(eventId); setToolMsg(null);
    try {
      const r = await axios.post(`${API}/admin/replay-webhook/${eventId}`);
      setToolMsg({ ok: true, text: `Replayed webhook (${r.data.type}). Handlers are idempotent — safe to re-run.` });
      loadEndpoint("webhook-events"); loadEndpoint("overview");
    } catch (e) { setToolMsg({ ok: false, text: e.response?.data?.detail || "Could not replay webhook." }); }
    setActing("");
  };

  if (state === "loading") return <div className="fcc-loading"><Loader2 className="spin" /></div>;
  if (state === "denied") return (
    <div className="fcc-denied" data-testid="adb-denied">
      <h1>Founder access only</h1>
      <p>The Control Center is restricted to the platform owner.</p>
      <button onClick={() => navigate("/dashboard")}>Back to Dashboard</button>
    </div>
  );

  const overview = store.overview || {};
  const health = store.health?.services || [];
  const aiu = store["ai-usage"] || {};
  const activity = store.activity?.activity || [];
  const generations = store.generations?.generations || [];
  const creditUsers = store.credits?.users || [];
  const creditHistory = store["credit-history"]?.transactions || [];
  const purchases = store.purchases?.purchases || [];
  const webhooks = store["webhook-events"]?.events || [];
  const failed = store["failed-generations"]?.failed || [];
  const wh = store["webhook-health"] || {};
  const emails = creditUsers.map((u) => u.email).filter(Boolean);

  const M = (r, k) => (r.metrics || {})[k];

  const genTable = (rows, opts = {}) => {
    const cols = [
      { key: "when", label: "When", sortable: true, val: (r) => r.finished_at || r.created_at || "", render: (r) => fmt(r.finished_at || r.created_at) },
      { key: "user", label: "User / Church", sortable: true, search: true, val: (r) => `${who(r.user)}${r.church ? " " + r.church : ""}`, render: (r) => `${who(r.user)}${r.church ? ` · ${r.church}` : ""}` },
      { key: "product", label: "Product", sortable: true, search: true, val: (r) => r.product || "" },
      { key: "status", label: "Status", sortable: true, val: (r) => (r.resolved ? "retried" : r.status), render: (r) => <span className={`fcc-badge s-${r.status}`}>{r.resolved ? "retried" : r.status}</span> },
      { key: "total", label: "Total", sortable: true, val: (r) => M(r, "total_ms") ?? -1, render: (r) => ms(M(r, "total_ms")) },
      { key: "ai", label: "AI", sortable: true, val: (r) => M(r, "ai_ms") ?? -1, render: (r) => ms(M(r, "ai_ms")) },
      { key: "tokens", label: "Tokens", sortable: true, val: (r) => M(r, "tokens_total") ?? -1, render: (r) => num(M(r, "tokens_total")) },
      { key: "cost", label: "Cost", sortable: true, val: (r) => M(r, "cost_usd") ?? -1, render: (r) => usd(M(r, "cost_usd")) },
      { key: "model", label: "Model", search: true, val: (r) => M(r, "model") || "", render: (r) => <span className="fcc-mono">{M(r, "model") || "—"}</span>, className: "fcc-mono" },
    ];
    if (opts.action) cols.push({ key: "action", label: "Action", render: (r) => (r.status === "failed" && !r.resolved
      ? <button className="fcc-mini" onClick={() => doRetry(r.job_id)} disabled={acting === r.job_id} data-testid={`adb-retry-${r.job_id}`}>{acting === r.job_id ? <Loader2 size={12} className="spin" /> : "Retry"}</button>
      : "—") });
    return (
      <DataTable testid={opts.testid} gridClass={opts.action ? "fcc-gen fcc-gen-a" : "fcc-gen"} columns={cols} rows={rows}
        rowKey={(r) => r.id || r.job_id} rowTestid={(r) => `fcc-gen-${r.job_id}`} csvName={opts.csvName}
        emptyText={opts.emptyText || "No generation records yet."}
        extraRow={opts.action && rows.some((r) => r.error) ? <div className="fcc-errline">{rows.filter((r) => r.error).slice(0, 4).map((r) => <div key={r.id}>⚠ {who(r.user)}: {r.error}</div>)}</div> : null} />
    );
  };

  const statCards = () => {
    const cards = [
      { label: "Users", v: overview.users_total }, { label: "Active Subs", v: overview.active_subscriptions },
      { label: "Reports Generated", v: overview.reports_generated }, { label: "Failed", v: overview.failed_generations },
      { label: "In Progress", v: overview.in_progress },
      { label: "Avg Gen Time", v: aiu.avg_generation_ms ? ms(aiu.avg_generation_ms) : "—" },
      { label: "Est. AI Cost", v: aiu.total_cost_usd != null ? `$${Number(aiu.total_cost_usd).toFixed(2)}` : "—" },
    ];
    return <div className="fcc-stats" data-testid="fcc-stats">{cards.map((c) => <div className="fcc-stat" key={c.label}><b>{c.v ?? "—"}</b><span>{c.label}</span></div>)}</div>;
  };

  const healthStrip = () => (
    <div className="fcc-health" data-testid="adb-health">
      {health.map((s) => (
        <div className={`fcc-hcard st-${(s.status || "").toLowerCase()}`} key={s.name} data-testid={`fcc-health-${s.name.split(" ")[0].toLowerCase()}`}>
          <div className="fcc-hstatus"><span className="fcc-dot" /> {s.status}</div>
          <div className="fcc-hname">{s.name}</div>
          <div className="fcc-hmeta">{s.avg_ms != null ? `${s.avg_ms}ms · ` : ""}{s.last_at ? `last ${fmt(s.last_at)}` : (s.note || "")}</div>
        </div>
      ))}
    </div>
  );

  const activityFeed = (limit) => (
    <div className="fcc-feed" data-testid="fcc-activity">
      {activity.length === 0 ? <div className="fcc-empty">No activity yet.</div> : activity.slice(0, limit || activity.length).map((a, i) => (
        <div className={`fcc-feed-row t-${a.type}`} key={i} data-testid="fcc-activity-row">
          <span className="fcc-feed-dot" />
          <span className="fcc-feed-label">{a.label}</span>
          <span className="fcc-feed-meta">{who(a.user)}{a.church ? ` · ${a.church}` : ""}{a.product ? ` · ${a.product}` : ""}{a.duration_ms != null ? ` · ${ms(a.duration_ms)}` : ""}</span>
          <span className="fcc-feed-ts">{fmt(a.ts)}</span>
        </div>
      ))}
    </div>
  );

  const creditTools = () => (
    <div className="fcc-tools" data-testid="adb-owner-tools">
      <datalist id="fcc-emails">{emails.map((e) => <option key={e} value={e} />)}</datalist>
      {toolMsg && <div className={`fcc-toolmsg ${toolMsg.ok ? "ok" : "err"}`} data-testid="adb-tool-msg">{toolMsg.text}</div>}
      <div className="fcc-tool-grid">
        <div className="fcc-tool-card" data-testid="adb-tool-grant">
          <h3>Grant Test Credit</h3>
          <input list="fcc-emails" placeholder="User email" value={grant.email} onChange={(e) => setGrant({ ...grant, email: e.target.value })} data-testid="adb-grant-email" />
          <div className="fcc-quick">{["1", "5"].map((a) => <button key={a} className={grant.amount === a ? "active" : ""} onClick={() => setGrant({ ...grant, amount: a })} data-testid={`adb-grant-preset-${a}`}>{a}</button>)}<input type="number" min="1" value={grant.amount} onChange={(e) => setGrant({ ...grant, amount: e.target.value })} data-testid="adb-grant-amount" /></div>
          <button className="fcc-primary" onClick={doGrant} data-testid="adb-grant-btn">Grant Credit</button>
        </div>
        <div className="fcc-tool-card" data-testid="adb-tool-remove">
          <h3>Remove Credit</h3>
          <input list="fcc-emails" placeholder="User email" value={remove.email} onChange={(e) => setRemove({ ...remove, email: e.target.value })} data-testid="adb-remove-email" />
          <div className="fcc-quick">{["1", "5"].map((a) => <button key={a} className={remove.amount === a ? "active" : ""} onClick={() => setRemove({ ...remove, amount: a })} data-testid={`adb-remove-preset-${a}`}>{a}</button>)}<input type="number" min="1" value={remove.amount} onChange={(e) => setRemove({ ...remove, amount: e.target.value })} data-testid="adb-remove-amount" /></div>
          <button className="fcc-danger" onClick={doRemove} data-testid="adb-remove-btn">Remove Credit</button>
        </div>
      </div>
    </div>
  );

  const renderView = () => {
    if (busy && !VIEW_ENDPOINTS[view].every((e) => store[e])) return <div className="fcc-empty"><Loader2 className="spin" size={18} /> Loading…</div>;

    switch (view) {
      case "dashboard":
        return (
          <>
            {statCards()}
            <h2 className="fcc-h2">System Health</h2>{healthStrip()}
            <div className="fcc-2col">
              <div><h2 className="fcc-h2">Recent Activity</h2>{activityFeed(10)}</div>
              <div><h2 className="fcc-h2">AI Snapshot</h2>{aiSnapshot()}</div>
            </div>
          </>
        );
      case "activity": return <>{activityFeed()}</>;
      case "jobs": return genTable(generations, { testid: "adb-table-jobs", csvName: "jobs.csv" });
      case "ai-usage": return aiUsageView();
      case "health": return <>
        {webhookHealthPanel()}
        <h2 className="fcc-h2">Service Monitor</h2>
        <div className="fcc-health-grid" data-testid="adb-table-health">{health.map((s) => (
        <div className={`fcc-hcard big st-${(s.status || "").toLowerCase()}`} key={s.name}>
          <div className="fcc-hstatus"><span className="fcc-dot" /> {s.status}</div>
          <div className="fcc-hname">{s.name}</div>
          <div className="fcc-hmeta">{s.note}</div>
          <div className="fcc-hmeta">Avg response: {s.avg_ms != null ? `${s.avg_ms}ms` : "—"}</div>
          <div className="fcc-hmeta">Last: {s.last_at ? fmt(s.last_at) : "—"}</div>
        </div>))}</div></>;
      case "users": return usersTable(creditUsers);
      case "credits": return <>{creditTools()}<h2 className="fcc-h2">Credit History</h2>{creditHistoryTable(creditHistory)}</>;
      case "subscriptions": return usersTable(creditUsers.filter((u) => ["parish_pro", "cathedral", "diocese"].includes(u.plan)), "adb-table-subscriptions");
      case "purchases":
      case "stripe": return purchasesTable(purchases);
      case "product-sundaybuilder": return genTable(generations.filter((g) => g.product === "sundaybuilder"), { testid: "adb-table-product-sundaybuilder" });
      case "product-parish": return genTable(generations.filter((g) => g.product === "parish_intelligence"), { testid: "adb-table-product-parish" });
      case "product-future": return <div className="fcc-placeholder" data-testid="adb-future"><Sparkles size={26} /><h3>Future Products</h3><p>Formation Intelligence™ and Diocesan Intelligence™ will report their telemetry here as they launch.</p></div>;
      case "webhooks": return webhookTable(webhooks);
      case "transactions":
      case "audit-log": return creditHistoryTable(creditHistory, true);
      case "failed-jobs": return genTable(failed, { testid: "adb-table-failed-generations", action: true });
      case "retry-queue": return genTable(generations.filter((g) => g.status === "started" || g.resolved), { testid: "adb-table-retry-queue" });
      default: return null;
    }
  };

  const runWebhookSelfTest = async () => {
    setTesting(true); setSelfTest(null);
    try {
      const r = await axios.post(`${API}/admin/billing/webhook-selftest`);
      setSelfTest({ ok: !!r.data.ok, ...r.data });
    } catch (e) {
      setSelfTest({ ok: false, message: e.response?.data?.detail || e.message || "Test failed." });
    }
    await loadEndpoint("webhook-health");
    setTesting(false);
  };

  const webhookHealthPanel = () => {
    const components = wh.components || [];
    const overallOk = components.length > 0 && components.every((c) => c.status === "healthy");
    const overallLabel = overallOk ? "All Systems Operational" : "Attention Required";

    const details = [
      ["Last webhook received", fmt(wh.last_received_at)],
      ["Last successful webhook", fmt(wh.last_successful_at)],
      ["Last failed webhook", fmt(wh.last_failed_at)],
      ["Last failure reason", wh.last_failure_reason || "—"],
      ["Failed webhooks (24h)", wh.failed_24h ?? "—"],
      ["Signature failures (24h)", wh.signature_failed_24h ?? "—"],
      ["Duplicate deliveries (24h)", wh.duplicates_24h ?? "—"],
      ["Processed (24h)", wh.processed_24h ?? "—"],
    ];

    const hasSuccess = !!wh.last_successful_at;
    const hasAnyActivity = !!wh.last_received_at;
    const emptyEventCopy = hasAnyActivity
      ? "No successful billing events in the last 24 hours."
      : "No billing activity yet.";

    return (
      <div className="fcc-whpanel" data-testid="adb-webhook-health">
        <div className="fcc-whhead">
          <h2 className="fcc-h2" style={{ margin: 0 }}>Billing Integration Health</h2>
          <span
            className={`fcc-badge ${overallOk ? "s-active" : "s-failed"}`}
            data-testid="adb-webhook-sig-status"
          >
            {overallLabel}
          </span>
          <button
            className="fcc-whtest-btn"
            onClick={runWebhookSelfTest}
            disabled={testing}
            data-testid="adb-webhook-run-test"
          >
            {testing ? <><Loader2 size={13} className="spin" /> Running…</> : <><PlayCircle size={13} /> Run Test</>}
          </button>
        </div>

        {wh.endpoint_warning && (
          <div className="fcc-whwarn" data-testid="adb-webhook-warning">
            <AlertTriangle size={16} /> {wh.endpoint_warning}
          </div>
        )}

        {selfTest && (
          <div
            className={`fcc-whtest-result ${selfTest.ok ? "ok" : "err"}`}
            data-testid="adb-webhook-test-result"
          >
            <b>{selfTest.ok ? "Test passed" : "Test failed"}</b>
            <span>{selfTest.message}</span>
            {selfTest.elapsed_ms != null && <em>({selfTest.elapsed_ms}ms)</em>}
          </div>
        )}

        {/* Business metric: last successful billing event */}
        <div className="fcc-whlastevent" data-testid="adb-webhook-last-event">
          <span className="fcc-whlastevent-label">Last Successful Billing Event</span>
          {hasSuccess ? (
            <div className="fcc-whlastevent-body">
              <span className="fcc-mono" data-testid="adb-webhook-last-event-type">
                {wh.last_successful_event_type || "—"}
              </span>
              <span className="fcc-whlastevent-ts" data-testid="adb-webhook-last-event-ts">
                {fmt(wh.last_successful_at)}
              </span>
            </div>
          ) : (
            <div className="fcc-whlastevent-empty" data-testid="adb-webhook-last-event-empty">
              {emptyEventCopy}
            </div>
          )}
        </div>

        {/* Component status table */}
        <div className="fcc-whcomponents" data-testid="adb-webhook-components">
          <div className="fcc-whcomp-th">
            <span>Component</span>
            <span>Status</span>
          </div>
          {components.map((c) => (
            <div className="fcc-whcomp-row" key={c.name} data-testid={`adb-webhook-comp-${c.name.replace(/\s+/g, "-").toLowerCase()}`}>
              <span className="fcc-whcomp-name">
                {c.name}
                {c.note && <em className="fcc-whcomp-note">{c.note}</em>}
              </span>
              <span className={`fcc-whcomp-status st-${c.status}`}>
                <span className="fcc-dot" />
                {c.status === "healthy" ? "Healthy" : "Attention Required"}
              </span>
            </div>
          ))}
        </div>

        {/* Expandable technical details */}
        <details className="fcc-whdetails" data-testid="adb-webhook-details">
          <summary data-testid="adb-webhook-details-summary">
            <ChevronDown size={14} className="fcc-whdetails-caret" />
            Technical Details
          </summary>
          <div className="fcc-whgrid">
            {details.map(([k, v]) => (
              <div className="fcc-whrow" key={k}><span>{k}</span><b>{v}</b></div>
            ))}
          </div>
          {!wh.secret_configured && (
            <p className="fcc-note">
              STRIPE_WEBHOOK_SECRET is not set in this environment — signatures are not being verified.
            </p>
          )}
        </details>
      </div>
    );
  };

  const aiSnapshot = () => (
    <div className="fcc-ai-mini" data-testid="fcc-ai-snapshot">
      <div className="fcc-mini"><b>{aiu.generations_today ?? "—"}</b><span>Today</span></div>
      <div className="fcc-mini"><b>{aiu.generations_this_month ?? "—"}</b><span>This Month</span></div>
      <div className="fcc-mini"><b>{aiu.success_rate != null ? `${aiu.success_rate}%` : "—"}</b><span>Success</span></div>
      <div className="fcc-mini"><b>{aiu.avg_generation_ms ? ms(aiu.avg_generation_ms) : "—"}</b><span>Avg Time</span></div>
    </div>
  );

  const aiUsageView = () => {
    const cards = [
      { label: "Generations Today", v: aiu.generations_today }, { label: "This Month", v: aiu.generations_this_month },
      { label: "Avg Generation Time", v: aiu.avg_generation_ms ? ms(aiu.avg_generation_ms) : "—" },
      { label: "Success Rate", v: aiu.success_rate != null ? `${aiu.success_rate}%` : "—" },
      { label: "Failed Jobs", v: aiu.failed_count }, { label: "Avg Tokens", v: num(aiu.avg_tokens) },
      { label: "Avg Cost / Gen", v: usd(aiu.avg_cost_per_generation) }, { label: "Total AI Cost", v: usd(aiu.total_cost_usd) },
    ];
    return (
      <div data-testid="adb-table-ai-usage">
        <div className="fcc-stats">{cards.map((c) => <div className="fcc-stat" key={c.label}><b>{c.v ?? "—"}</b><span>{c.label}</span></div>)}</div>
        {aiu.cost_estimated && <p className="fcc-note">Token counts &amp; costs are ESTIMATES (derived from prompt/response length × public model pricing).</p>}
        <div className="fcc-2col">
          <div><h2 className="fcc-h2">Requests by Product</h2>
            <div className="fcc-table"><div className="fcc-tr fcc-th"><span>Product</span><span>Count</span><span>OK</span><span>Failed</span><span>Avg</span><span>Cost</span></div>
              {Object.entries(aiu.by_product || {}).map(([p, d]) => (
                <div className="fcc-tr" key={p}><span>{p}</span><span>{d.count}</span><span>{d.succeeded}</span><span>{d.failed}</span><span>{ms(d.avg_ms)}</span><span>{usd(d.cost_usd)}</span></div>
              ))}
            </div>
          </div>
          <div><h2 className="fcc-h2">Model Usage</h2>
            <div className="fcc-table"><div className="fcc-tr fcc-th"><span>Model</span><span>Calls</span></div>
              {Object.entries(aiu.model_usage || {}).map(([m, c]) => <div className="fcc-tr" key={m}><span className="fcc-mono">{m}</span><span>{c}</span></div>)}
            </div>
          </div>
        </div>
        <h2 className="fcc-h2">Recent AI Calls</h2>{genTable(aiu.recent || [], { testid: "adb-table-ai-recent", csvName: "ai-usage.csv" })}
      </div>
    );
  };

  const usersTable = (rows, testid = "adb-table-credits", csvName = "credits.csv") => {
    const cols = [
      { key: "user", label: "User", sortable: true, search: true, val: (r) => r.email || r.name || r.user_id, render: (r) => r.email || r.name || r.user_id },
      { key: "plan", label: "Plan", sortable: true, search: true, val: (r) => r.plan || "free" },
      { key: "status", label: "Status", sortable: true, val: (r) => r.plan_status || "inactive", render: (r) => <span className={`fcc-badge s-${r.plan_status || "inactive"}`}>{r.plan_status || "—"}</span> },
      { key: "credits", label: "Credits", sortable: true, val: (r) => r.single_report_credits ?? 0 },
      { key: "pi", label: "PI Used", val: (r) => (r.parish_intelligence_used ? "Yes" : "No") },
      { key: "sb", label: "SB Used", val: (r) => (r.sundaybuilder_used ? "Yes" : "No") },
    ];
    return <DataTable testid={testid} columns={cols} rows={rows} rowKey={(r) => r.user_id} rowTestid={(r) => `adb-credit-${r.user_id}`} csvName={csvName} emptyText="No users to show." />;
  };

  const creditHistoryTable = (rows, isAudit) => {
    const cols = [
      { key: "when", label: "When", sortable: true, val: (r) => r.created_at || "", render: (r) => fmt(r.created_at) },
      { key: "user", label: "User", sortable: true, search: true, val: (r) => who(r.user), render: (r) => who(r.user) },
      { key: "type", label: "Type", sortable: true, search: true, val: (r) => r.type || "", render: (r) => <span className={`fcc-badge t-${r.type}`}>{r.type}</span> },
      { key: "product", label: "Product", sortable: true, search: true, val: (r) => r.product || "—" },
      { key: "source", label: "Source", sortable: true, search: true, val: (r) => r.source || "—" },
      { key: "delta", label: "Δ", sortable: true, val: (r) => r.delta ?? 0, render: (r) => <span className={r.delta > 0 ? "pos" : r.delta < 0 ? "neg" : ""}>{r.delta > 0 ? `+${r.delta}` : r.delta}</span> },
    ];
    return <DataTable testid={isAudit ? "adb-table-audit-log" : "adb-table-credit-history"} gridClass="fcc-6" columns={cols} rows={rows} rowKey={(r) => r.id} rowTestid={(r) => `adb-txn-${r.id}`} csvName={isAudit ? "audit-log.csv" : "credit-history.csv"} emptyText="No credit transactions yet." />;
  };

  const purchasesTable = (rows) => {
    const cols = [
      { key: "when", label: "When", sortable: true, val: (r) => r.paid_at || r.created_at || "", render: (r) => fmt(r.paid_at || r.created_at) },
      { key: "user", label: "User", sortable: true, search: true, val: (r) => who(r.user), render: (r) => who(r.user) },
      { key: "plan", label: "Plan", sortable: true, search: true, val: (r) => r.plan || r.plan_name || "—" },
      { key: "status", label: "Status", sortable: true, val: (r) => r.status || "", render: (r) => <span className={`fcc-badge s-${r.status === "paid" ? "active" : "inactive"}`}>{r.status}</span> },
      { key: "session", label: "Session", search: true, val: (r) => r.session_id || "", render: (r) => <span className="fcc-mono">{(r.session_id || "—").slice(0, 18)}</span> },
    ];
    return <DataTable testid="adb-table-purchases" gridClass="fcc-5" columns={cols} rows={rows} rowKey={(r, i) => r.id || r.session_id} csvName="purchases.csv" emptyText="No purchases yet." />;
  };

  const webhookTable = (rows) => {
    const cols = [
      { key: "received", label: "Received", sortable: true, val: (r) => r.received_at || "", render: (r) => fmt(r.received_at) },
      { key: "type", label: "Type", sortable: true, search: true, val: (r) => r.type || "" },
      { key: "status", label: "Status", sortable: true, val: (r) => r.status || "", render: (r) => <span className={`fcc-badge s-${r.status === "processed" ? "active" : "inactive"}`}>{r.status || "—"}{r.replay_count ? ` ·↻${r.replay_count}` : ""}</span> },
      { key: "eid", label: "Event ID", search: true, val: (r) => r.event_id || "", render: (r) => <span className="fcc-mono">{(r.event_id || "—").slice(0, 20)}</span> },
      { key: "action", label: "Action", render: (r) => r.object ? <button className="fcc-mini" onClick={() => doReplay(r.event_id)} disabled={acting === r.event_id} data-testid={`adb-replay-${r.event_id}`}>{acting === r.event_id ? <Loader2 size={12} className="spin" /> : "Replay"}</button> : "—" },
    ];
    return <DataTable testid="adb-table-webhook-events" gridClass="fcc-5" columns={cols} rows={rows} rowKey={(r, i) => r.event_id} emptyText="No webhook events received yet." />;
  };

  const activeLabel = NAV.flatMap((s) => s.items).find((i) => i.key === view)?.label || "Dashboard";

  return (
    <div className="fcc" data-testid="admin-billing-dashboard">
      <aside className="fcc-side">
        <div className="fcc-brand">
          <img src="/brand/li-logo-sm.png" alt="Liturgical Intelligence™" onError={(e) => { e.target.style.display = "none"; }} />
          <div><b>Liturgical Intelligence™</b><span>Founder Control Center</span></div>
        </div>
        <nav className="fcc-nav" data-testid="fcc-nav">
          {NAV.map((sec) => (
            <div className="fcc-navsec" key={sec.section}>
              <button className="fcc-navsec-h" onClick={() => setCollapsed((c) => ({ ...c, [sec.section]: !c[sec.section] }))} data-testid={`fcc-navsec-${sec.section.toLowerCase()}`}>
                {sec.section}<ChevronDown size={13} className={collapsed[sec.section] ? "rot" : ""} />
              </button>
              {!collapsed[sec.section] && sec.items.map((it) => {
                const Icon = it.icon;
                return (
                  <button key={it.key} className={`fcc-navitem${view === it.key ? " active" : ""}`} onClick={() => setView(it.key)} data-testid={`fcc-nav-${it.key}`}>
                    <Icon size={15} /> {it.label}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
        <button className="fcc-exit" onClick={() => navigate("/dashboard")}>← Exit to App</button>
      </aside>

      <main className="fcc-main">
        <header className="fcc-top">
          <h1 data-testid="fcc-view-title">{activeLabel}</h1>
          <div className="fcc-quickbar" data-testid="fcc-quick-actions">
            <button onClick={doTestGen} data-testid="fcc-qa-testgen"><PlayCircle size={14} /> Test Report</button>
            <button onClick={() => { setView("credits"); }} data-testid="fcc-qa-grant"><Coins size={14} /> Grant</button>
            <button onClick={() => { setView("credits"); }} data-testid="fcc-qa-remove"><Coins size={14} /> Remove</button>
            <button onClick={() => setView("webhooks")} data-testid="fcc-qa-replay"><Webhook size={14} /> Replay</button>
            <button onClick={() => setView("failed-jobs")} data-testid="fcc-qa-retry"><Zap size={14} /> Retry</button>
            <button onClick={refresh} data-testid="fcc-qa-refresh"><RefreshCw size={14} className={busy ? "spin" : ""} /> Refresh</button>
            <button onClick={() => navigate("/admin/founding-circle")} data-testid="adb-founding-link"><DollarSign size={14} /> Founding</button>
            <button onClick={() => navigate("/admin/annual-planning-qa")} data-testid="adb-annual-planning-qa-link"><Zap size={14} /> Annual Planning QA</button>
            <button onClick={() => navigate("/admin/repertoire")} data-testid="adb-repertoire-link"><Zap size={14} /> Resource Library</button>
          </div>
        </header>

        {testGen && (
          <div className={`fcc-testgen s-${testGen.status}`} data-testid="adb-testgen-status">
            <div className="fcc-tg-left"><b>Test Generation</b>
              <span className="fcc-tg-state" data-testid="adb-testgen-state">
                {testGen.status === "starting" && <><Loader2 size={12} className="spin" /> Starting…</>}
                {testGen.status === "in_progress" && <><Loader2 size={12} className="spin" /> In progress</>}
                {testGen.status === "completed" && "Completed"}
                {testGen.status === "failed" && "Failed"}
              </span>
            </div>
            <div className="fcc-tg-right">
              {testGen.jobId && <span className="fcc-mono" data-testid="adb-testgen-jobid">{testGen.jobId}</span>}
              {testGen.status === "completed" && <a className="fcc-report-btn" href={`${API}/parish/job/${testGen.jobId}`} target="_blank" rel="noopener noreferrer" data-testid="adb-testgen-report-link"><ExternalLink size={14} /> View Finished Report</a>}
              {testGen.status === "failed" && <span className="fcc-tg-err" data-testid="adb-testgen-error">{testGen.error}</span>}
              <button className="fcc-tg-x" onClick={() => setTestGen(null)}>✕</button>
            </div>
          </div>
        )}

        <section className="fcc-content">{renderView()}</section>
      </main>
    </div>
  );
}
